import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Money, Kpi } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";
import { TaxPanel } from "@/components/TaxPanel";

const Line = ({ label, v, bold, testid }) => (
  <div className={`flex justify-between gap-4 border-b border-slate-50 py-1.5 text-sm ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`} data-testid={testid}>
    <span>{label}</span><Money v={v} />
  </div>
);

const Side = ({ title, sections, total, testid }) => {
  const { t } = useApp();
  return (
    <Panel title={title} testid={testid}>
      {sections.map((s) => (
        <div key={s.section} className="mb-4">
          <div className="eyebrow mb-1">{s.section}</div>
          {s.items.map((i) => <Line key={i.label} label={i.label} v={i.net} />)}
          <Line label={`${t("total")} ${s.section.toLowerCase()}`} v={s.total} bold />
        </div>
      ))}
      <div className="flex justify-between rounded-lg bg-slate-900 px-3 py-2 text-sm font-bold text-white"><span>{t("total")}</span><Money v={total} /></div>
    </Panel>
  );
};

const Income = ({ inc }) => {
  const { t } = useApp();
  return (
    <Panel title={t("income_statement")} testid="closing-income">
      {inc.rows.map((r) => <Line key={r.label} label={`${r.kind === "income" ? "+" : "−"} ${r.label}`} v={r.amount} />)}
      <Line label={t("operating_result")} v={inc.operating_result} bold />
      <Line label={t("ordinary_result")} v={inc.ordinary_result_before_tax} bold />
      <div className={`mt-2 flex justify-between rounded-lg px-3 py-2 text-sm font-bold ${inc.net_result >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="closing-net-result">
        <span>{t("net_result")}</span><Money v={inc.net_result} />
      </div>
    </Panel>
  );
};

export default function Closing() {
  const { api, t, can, company, download } = useApp();
  const [years, setYears] = useState(null);
  const [year, setYear] = useState(null);
  const [st, setSt] = useState(null);
  const loadYears = useCallback(() => api.get("/closing").then((r) => { setYears(r.data); setYear((y) => y || r.data[0]?.year); }), [api]);
  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { if (year) { setSt(null); api.get(`/closing/${year}/statements`).then((r) => setSt(r.data)); } }, [api, year, years]);
  const act = async (kind) => {
    if (!window.confirm(t(kind === "close" ? "closing_confirm" : "reopen_confirm"))) return;
    try { await api.post(`/closing/${year}/${kind}`); toast.success(t(kind === "close" ? "closing_done" : "reopen_done")); loadYears(); } catch (e) { toast.error(errMsg(e)); }
  };
  const info = years?.find((y) => y.year === year);
  const thisYear = new Date().getFullYear();
  return (
    <>
      <PageHeader eyebrow={t("accounting")} title={t("closing")}>
        {st && <Button variant="outline" onClick={() => download(`/closing/${year}/statements.pdf`, `etats-financiers-${year}.pdf`)} data-testid="closing-pdf"><Download className="h-4 w-4 me-1" />PDF</Button>}
        {info && !info.closed && year < thisYear && can("accounting", true) && <Button onClick={() => act("close")} data-testid="closing-close-btn"><Lock className="h-4 w-4 me-1" />{t("closing_close")} {year}</Button>}
        {info?.closed && company?.role === "owner" && <Button variant="outline" onClick={() => act("reopen")} data-testid="closing-reopen-btn"><Unlock className="h-4 w-4 me-1" />{t("closing_reopen")}</Button>}
      </PageHeader>
      {!years ? <Loading /> : (
        <div className="flex flex-wrap gap-2" data-testid="closing-years">
          {years.map((y) => (
            <button key={y.year} onClick={() => setYear(y.year)} className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-colors ${y.year === year ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`} data-testid={`closing-year-${y.year}`}>
              {y.closed ? <Lock className="h-3.5 w-3.5" /> : null}{y.year}
            </button>
          ))}
        </div>
      )}
      {info?.closed && <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="closing-closed-info"><CheckCircle2 className="h-4 w-4" />{t("closing_closed_on")} {dmy(info.closed_at)} · {info.closed_by}</p>}
      {st && !st.closed && <p className="text-xs text-orange-700">{t("closing_provisional")}</p>}
      {!st ? <Loading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Kpi label={t("total_assets")} value={<Money v={st.balance_sheet.total_assets} />} testid="closing-kpi-assets" />
            <Kpi label={t("net_result")} value={<Money v={st.income_statement.net_result} />} accent={st.income_statement.net_result >= 0 ? "teal" : "red"} testid="closing-kpi-result" />
            <Kpi label={t("closing_balance_check")} value={st.balance_sheet.balanced ? "✓" : "✗"} sub={st.balance_sheet.balanced ? t("closing_balanced") : t("closing_unbalanced")} accent="navy" testid="closing-kpi-balanced" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Side title={t("bs_assets")} sections={st.balance_sheet.assets} total={st.balance_sheet.total_assets} testid="closing-assets" />
            <Side title={t("bs_liabilities")} sections={st.balance_sheet.liabilities} total={st.balance_sheet.total_liabilities} testid="closing-liabilities" />
          </div>
          <Income inc={st.income_statement} />
          <TaxPanel year={year} />
          <p className="text-xs text-slate-500">{t("disclaimer")}</p>
        </>
      )}
    </>
  );
}
